// ═══════════════════════════════════════════════════════════════════
//  TOQUM · SUPPLIER PORTAL  (full redesign)
//  Tabs: Dashboard · My POs · Production · Inspection Offer · Documents
// ═══════════════════════════════════════════════════════════════════
import React, { useState, useMemo, useCallback } from 'react';
import { T, uid, today, num, uploadFile, Section, Btn, Tag, Modal, Toast, API_URL } from './Shared';

const STAGES = ['Cutting','Sewing','Washing','Finishing','Packing'];

// ── Helpers ──────────────────────────────────────────────────────
const Badge = ({ s }) => {
  const map = {
    Passed:'#e9f9f0|#0a7d43', Failed:'#fdeaec|#b4232f', Submitted:'#fff8e6|#8a5a00',
    Approved:'#e9f9f0|#0a7d43', Rejected:'#fdeaec|#b4232f', Pending:'#eef4ff|#0073ea',
    'Under Review':'#eef4ff|#0073ea', Accepted:'#e9f9f0|#0a7d43',
  };
  const [bg,color] = (map[s]||'#f4f6fa|#676879').split('|');
  return <span style={{ padding:'2px 10px', borderRadius:10, fontSize:10.5, fontWeight:800,
    background:bg, color, whiteSpace:'nowrap' }}>{s||'Pending'}</span>;
};

const KPI = ({ label, value, sub, color='#0073ea', icon='' }) => (
  <div style={{ background:'white', border:`1px solid #e6e9ef`, borderLeft:`4px solid ${color}`,
    borderRadius:10, padding:'14px 16px', flex:'1 1 140px', minWidth:130 }}>
    <div style={{ fontSize:9.5, color:'#676879', textTransform:'uppercase',
      letterSpacing:'0.06em', marginBottom:3 }}>{icon} {label}</div>
    <div style={{ fontSize:26, fontWeight:900, color:'#1b1f26', lineHeight:1 }}>{value}</div>
    {sub && <div style={{ fontSize:10.5, color:'#676879', marginTop:3 }}>{sub}</div>}
  </div>
);

const ProgBar = ({ value, max, color='#0073ea' }) => {
  const pct = max ? Math.min(100, Math.round(value/max*100)) : 0;
  return (
    <div style={{ display:'flex', alignItems:'center', gap:8 }}>
      <div style={{ flex:1, height:8, background:'#f1f5f9', borderRadius:4, overflow:'hidden' }}>
        <div style={{ height:'100%', width:`${pct}%`, background:color, borderRadius:4 }}/>
      </div>
      <span style={{ fontSize:11, fontWeight:700, color, minWidth:35 }}>{pct}%</span>
    </div>
  );
};

// ── FILE UPLOADER BUTTON ─────────────────────────────────────────
function UploadBtn({ label, onDone, accept='.pdf,.jpg,.png,.xlsx,.csv,.doc,.docx', meta={} }) {
  const [busy, setBusy] = useState(false);
  return (
    <label style={{ display:'inline-flex', alignItems:'center', gap:6, padding:'8px 14px',
      background: busy?'#cbd5e1':'white', border:`1.5px solid ${T.primary}`, color:T.primary,
      borderRadius:7, fontSize:12, fontWeight:700, cursor:busy?'not-allowed':'pointer',
      transition:'all .15s' }}>
      {busy ? '⏳ Uploading…' : `📎 ${label}`}
      <input type="file" accept={accept} style={{ display:'none' }} disabled={busy}
        onChange={async e => {
          const f = e.target.files[0]; if (!f) return;
          setBusy(true);
          const r = await uploadFile(f, { category:'supplierDoc', docType:label, ...meta });
          setBusy(false);
          if (r?.url) onDone({ name:f.name, url:r.url.startsWith('http')?r.url:'https://'+r.url.replace(/^\/+/,''), size:f.size });
          else alert('Upload failed — try again');
          e.target.value='';
        }}/>
    </label>
  );
}

// ════════════════════════════════════════════════════════════════
export default function SupplierPortalHome({ db, setDb, saveToDB, currentUser }) {
  const supplierName = currentUser.name;
  const myPOs   = useMemo(()=>(db.pos||[]).filter(p=>p.supplier===supplierName),[db.pos,supplierName]);
  const myLogs  = useMemo(()=>(db.productionLogs||[]).filter(l=>l.supplier===supplierName),[db.productionLogs,supplierName]);
  const myInsp  = useMemo(()=>(db.inspections||[]).filter(i=>i.supplier===supplierName),[db.inspections,supplierName]);
  const myOffers= useMemo(()=>(db.inspectionOffers||[]).filter(o=>o.supplier===supplierName),[db.inspectionOffers,supplierName]);
  const myDocs  = useMemo(()=>(db.supplierSubmissions||[]).filter(d=>d.supplier===supplierName),[db.supplierSubmissions,supplierName]);

  const [tab, setTab]   = useState('DASHBOARD');
  const [selPO, setSelPO] = useState('');
  const [toast, setToast] = useState(null);
  const notify = useCallback((msg,type='success')=>{
    setToast({msg,type}); setTimeout(()=>setToast(null),3500);
  },[]);

  const todayStr     = today();
  const yesterdayStr = new Date(Date.now()-86400000).toISOString().split('T')[0];

  // ── KPI calculations ─────────────────────────────────────────
  const totalInsp    = myInsp.length;
  const passed       = myInsp.filter(i=>i.result==='Passed').length;
  const fpr          = totalInsp ? Math.round(passed/totalInsp*100) : null;
  const totalDefects = myInsp.reduce((a,i)=>a+num(i.totals?.critical)+num(i.totals?.major)+num(i.totals?.minor),0);
  const totalChecked = myInsp.reduce((a,i)=>a+num(i.header?.visualCheckQty||i.totals?.checked),0);
  const dhu          = totalChecked ? (totalDefects/totalChecked*100).toFixed(1) : null;
  const openCaps     = (db.caps||[]).filter(c=>c.supplier===supplierName&&c.status!=='Closed').length;
  const rejectedLogs = myLogs.filter(l=>l.status==='Rejected');

  const TABS = ['DASHBOARD','MY POs','PRODUCTION','INSPECTION OFFER','DOCUMENTS'];

  // ════════════════════════════════════════════════════════════════
  // TAB: DASHBOARD
  // ════════════════════════════════════════════════════════════════
  const DashboardTab = () => {
    const recent = [...myInsp].sort((a,b)=>b.date.localeCompare(a.date)).slice(0,5);
    const defByType = myInsp.reduce((acc,i)=>{
      (i.defects||[]).forEach(d=>{ acc[d.defect]=(acc[d.defect]||0)+num(d.qty); }); return acc;
    },{});
    const topDefects = Object.entries(defByType).sort((a,b)=>b[1]-a[1]).slice(0,5);

    return (
      <div>
        {/* Rejection alert */}
        {rejectedLogs.length > 0 && (
          <div style={{ background:'#fdeaec', border:'1px solid #e2445c', borderRadius:8,
            padding:'10px 14px', marginBottom:14, fontSize:12.5, color:'#b4232f' }}>
            ⚠ <b>{rejectedLogs.length} production entry(ies) rejected by QA</b> — go to Production tab to correct.
            <div style={{ marginTop:5, fontSize:11 }}>
              {rejectedLogs.slice(0,3).map(l=>(
                <div key={l.id}>• PO {l.poNo} · {l.date} · {l.stage} {l.color} {l.size}
                  {l.rejectionReason && ` — "${l.rejectionReason}"`}</div>
              ))}
            </div>
          </div>
        )}

        {/* KPI row */}
        <div style={{ display:'flex', gap:12, flexWrap:'wrap', marginBottom:20 }}>
          <KPI label="First Pass Rate" value={fpr!==null?`${fpr}%`:'—'}
            color={fpr>=80?T.success:fpr>=60?T.warning:T.danger} icon="✅"
            sub={`${passed} of ${totalInsp} inspections passed`}/>
          <KPI label="DHU" value={dhu||'—'} color={dhu<=2.5?T.success:dhu<=5?T.warning:T.danger}
            icon="📊" sub="Defects per 100 units checked"/>
          <KPI label="Open POs" value={myPOs.length} color={T.primary} icon="📦"
            sub={`${myLogs.length} production entries`}/>
          <KPI label="Open CAPs" value={openCaps} color={openCaps>0?T.danger:T.success}
            icon="⚠️" sub="Corrective actions pending"/>
          <KPI label="Pending Offers" value={myOffers.filter(o=>o.status==='Pending').length}
            color={T.warning} icon="🔍" sub="Awaiting QA response"/>
        </div>

        {/* Stage progress for each PO */}
        {myPOs.length > 0 && (
          <div style={{ background:'white', border:`1px solid #e6e9ef`, borderRadius:10,
            padding:'14px 16px', marginBottom:18 }}>
            <div style={{ fontSize:11, fontWeight:800, textTransform:'uppercase',
              letterSpacing:'0.06em', color:'#676879', marginBottom:12 }}>Production Progress by PO</div>
            {myPOs.slice(0,5).map(p => {
              const pLogs = myLogs.filter(l=>l.poNo===p.poNo);
              const pqty  = num(p.qty);
              return (
                <div key={p.poNo} style={{ marginBottom:14 }}>
                  <div style={{ display:'flex', justifyContent:'space-between', marginBottom:4 }}>
                    <span style={{ fontWeight:700, fontSize:12.5 }}>PO {p.poNo} · {p.styleNo}</span>
                    <span style={{ fontSize:11, color:'#676879' }}>{pqty.toLocaleString()} pcs · {p.brand}</span>
                  </div>
                  {STAGES.map(stage => {
                    const qty = pLogs.filter(l=>l.stage===stage).reduce((a,l)=>a+num(l.qty),0);
                    if (!qty) return null;
                    return (
                      <div key={stage} style={{ display:'flex', alignItems:'center', gap:8, marginBottom:4 }}>
                        <span style={{ fontSize:10.5, color:'#676879', width:80, flexShrink:0 }}>{stage}</span>
                        <ProgBar value={qty} max={pqty}
                          color={qty>=pqty?T.success:stage==='Packing'?T.warning:T.primary}/>
                        <span style={{ fontSize:10.5, color:'#676879', flexShrink:0 }}>
                          {qty.toLocaleString()} pcs
                        </span>
                      </div>
                    );
                  })}
                </div>
              );
            })}
          </div>
        )}

        {/* Top defects */}
        {topDefects.length > 0 && (
          <div style={{ background:'white', border:`1px solid #e6e9ef`, borderRadius:10,
            padding:'14px 16px', marginBottom:18 }}>
            <div style={{ fontSize:11, fontWeight:800, textTransform:'uppercase',
              letterSpacing:'0.06em', color:'#676879', marginBottom:10 }}>Top Defects Found</div>
            {topDefects.map(([defect,qty])=>(
              <div key={defect} style={{ display:'flex', alignItems:'center', gap:8, marginBottom:6 }}>
                <span style={{ fontSize:12, flex:1 }}>{defect}</span>
                <ProgBar value={qty} max={topDefects[0][1]} color={T.danger}/>
                <span style={{ fontSize:11, fontWeight:700, color:T.danger, flexShrink:0 }}>{qty}</span>
              </div>
            ))}
          </div>
        )}

        {/* Recent inspections */}
        {recent.length > 0 && (
          <div style={{ background:'white', border:`1px solid #e6e9ef`, borderRadius:10, overflow:'hidden' }}>
            <div style={{ padding:'11px 16px', background:'linear-gradient(90deg,#f8fafc,#f1f5f9)',
              borderBottom:'1px solid #e6e9ef', fontSize:11, fontWeight:800,
              textTransform:'uppercase', letterSpacing:'0.06em', color:'#323338' }}>
              Recent Inspections
            </div>
            <div style={{ overflowX:'auto' }}>
              <table style={{ borderCollapse:'collapse', fontSize:12, width:'100%' }}>
                <thead><tr style={{ background:'#f8fafc' }}>
                  {['Date','PO','Stage','QA','Cri/Maj/Min','Result'].map(h=>(
                    <th key={h} style={{ padding:'7px 10px', textAlign:'left', fontWeight:800,
                      borderBottom:'1px solid #e6e9ef', fontSize:11, color:'#676879' }}>{h}</th>
                  ))}
                </tr></thead>
                <tbody>{recent.map(r=>(
                  <tr key={r.id} style={{ borderBottom:'1px solid #f1f5f9' }}>
                    <td style={{ padding:'7px 10px' }}>{r.date}</td>
                    <td style={{ padding:'7px 10px', fontWeight:700 }}>{r.poNo}</td>
                    <td style={{ padding:'7px 10px' }}>{r.stage}</td>
                    <td style={{ padding:'7px 10px', color:'#676879', fontSize:11 }}>{r.qaMember}</td>
                    <td style={{ padding:'7px 10px' }}>
                      <span style={{ color:num(r.totals?.critical)>0?T.danger:'inherit', fontWeight:700 }}>
                        {num(r.totals?.critical)}</span>/
                      <span style={{ color:num(r.totals?.major)>0?T.warning:'inherit' }}>
                        {num(r.totals?.major)}</span>/
                      {num(r.totals?.minor)}
                    </td>
                    <td style={{ padding:'7px 10px' }}><Badge s={r.result||r.status}/></td>
                  </tr>
                ))}</tbody>
              </table>
            </div>
          </div>
        )}

        {totalInsp===0 && myPOs.length===0 && (
          <div style={{ textAlign:'center', padding:'40px 20px', color:'#676879' }}>
            <div style={{ fontSize:40, marginBottom:12 }}>🏭</div>
            <div style={{ fontSize:14, fontWeight:700 }}>Welcome to your supplier portal</div>
            <div style={{ fontSize:12, marginTop:6 }}>Your POs, production data and inspection results will appear here.</div>
          </div>
        )}
      </div>
    );
  };

  // ════════════════════════════════════════════════════════════════
  // TAB: MY POs
  // ════════════════════════════════════════════════════════════════
  const POsTab = () => (
    <div>
      {myPOs.length===0
        ? <div style={{textAlign:'center',padding:40,color:'#676879',fontSize:13}}>
            No purchase orders found. Contact your QA manager.
          </div>
        : myPOs.map(p=>{
          const pLogs  = myLogs.filter(l=>l.poNo===p.poNo);
          const packed = pLogs.filter(l=>l.stage==='Packing').reduce((a,l)=>a+num(l.qty),0);
          const pqty   = num(p.qty);
          const offer  = myOffers.find(o=>o.poNo===p.poNo);
          const insp   = myInsp.filter(i=>i.poNo===p.poNo);
          return (
            <div key={p.poNo} style={{ background:'white', border:'1px solid #e6e9ef',
              borderRadius:12, overflow:'hidden', marginBottom:14,
              boxShadow:'0 1px 4px rgba(0,0,0,.05)' }}>
              {/* PO Header */}
              <div style={{ background:'linear-gradient(90deg,#12161c,#1a2236)',
                padding:'12px 16px', color:'white', display:'flex',
                justifyContent:'space-between', alignItems:'center', flexWrap:'wrap', gap:8 }}>
                <div>
                  <span style={{ fontWeight:900, fontSize:14 }}>PO {p.poNo}</span>
                  <span style={{ marginLeft:10, fontSize:12, opacity:0.8 }}>
                    {p.styleNo} · {p.brand}
                  </span>
                </div>
                <div style={{ display:'flex', gap:8 }}>
                  <button onClick={()=>{ setSelPO(p.poNo); setTab('PRODUCTION'); }}
                    style={{ padding:'6px 14px', background:'#0073ea', color:'white', border:'none',
                      borderRadius:6, fontSize:11, fontWeight:700, cursor:'pointer' }}>
                    📊 Production
                  </button>
                  <button onClick={()=>{ setSelPO(p.poNo); setTab('INSPECTION OFFER'); }}
                    style={{ padding:'6px 14px', background:'#00c875', color:'white', border:'none',
                      borderRadius:6, fontSize:11, fontWeight:700, cursor:'pointer' }}>
                    🔍 Offer Inspection
                  </button>
                </div>
              </div>

              <div style={{ padding:'12px 16px' }}>
                {/* Key details */}
                <div style={{ display:'grid', gridTemplateColumns:'repeat(auto-fill,minmax(140px,1fr))', gap:'6px 16px', fontSize:11.5, marginBottom:12 }}>
                  {[['PO Qty', `${pqty.toLocaleString()} pcs`],
                    ['Ex-Factory', p.exFactory||'—'],
                    ['Packed', `${packed.toLocaleString()} pcs`],
                    ['Inspections', insp.length],
                    ['Last Result', insp[0]?.result||'None yet'],
                    ['Offer Status', offer?.status||'Not offered'],
                  ].map(([k,v])=>(
                    <div key={k}><span style={{color:'#676879'}}>{k}: </span><b>{v}</b></div>
                  ))}
                </div>

                {/* Packing progress */}
                <div style={{ marginBottom:10 }}>
                  <div style={{ display:'flex', justifyContent:'space-between', marginBottom:4 }}>
                    <span style={{ fontSize:11, color:'#676879' }}>Packing Progress</span>
                    <span style={{ fontSize:11, fontWeight:700 }}>{packed.toLocaleString()} / {pqty.toLocaleString()}</span>
                  </div>
                  <ProgBar value={packed} max={pqty}
                    color={packed>=pqty?T.success:packed>pqty*0.5?T.warning:T.danger}/>
                </div>

                {/* Colors & sizes */}
                {(p.matrix||[]).length > 0 && (
                  <div style={{ display:'flex', flexWrap:'wrap', gap:5 }}>
                    {[...new Set(p.matrix.map(m=>m.color).filter(Boolean))].map(c=>(
                      <span key={c} style={{ background:'#eef4ff', color:'#0073ea',
                        padding:'2px 9px', borderRadius:8, fontSize:11, fontWeight:700 }}>{c}</span>
                    ))}
                    {[...new Set(p.matrix.map(m=>m.size).filter(Boolean))].map(s=>(
                      <span key={s} style={{ background:'#f0fdf4', color:T.success,
                        padding:'2px 9px', borderRadius:8, fontSize:11, fontWeight:700 }}>{s}</span>
                    ))}
                  </div>
                )}
              </div>
            </div>
          );
        })
      }
    </div>
  );

  // ════════════════════════════════════════════════════════════════
  // TAB: PRODUCTION
  // ════════════════════════════════════════════════════════════════
  const ProductionTab = () => {
    const [lines, setLines]   = useState([]);
    const [saving, setSaving] = useState(false);
    const po      = myPOs.find(p=>p.poNo===selPO);
    const colors  = [...new Set((po?.matrix||[]).map(m=>m.color).filter(Boolean))];
    const sizes   = [...new Set((po?.matrix||[]).map(m=>m.size).filter(Boolean))];
    const poLogs  = myLogs.filter(l=>l.poNo===selPO);

    const addLines = () => {
      // Auto-generate one row per color+size from PO matrix
      if (po?.matrix?.length && !lines.length) {
        setLines(po.matrix.map(m=>({
          stage:'Packing', line:'', color:m.color||'', size:m.size||'',
          qty:'', date:todayStr
        })));
      } else {
        setLines(p=>[...p,{ stage:'Packing', line:'', color:colors[0]||'',
          size:sizes[0]||'', qty:'', date:todayStr }]);
      }
    };

    const upd = (i,k,v) => setLines(p=>p.map((x,xi)=>xi===i?{...x,[k]:v}:x));

    const submit = async () => {
      const valid = lines.filter(l=>parseInt(l.qty)>0);
      if (!valid.length) return alert('Enter qty for at least one line');
      setSaving(true);
      const entries = valid.map(l=>({
        id:uid(), poNo:selPO, styleNo:po?.styleNo||'', supplier:supplierName,
        stage:l.stage, line:l.line, color:l.color, size:l.size,
        qty:parseInt(l.qty)||0, date:l.date,
        enteredBy:currentUser.name, enteredAt:new Date().toISOString(),
        status:'Submitted', history:[],
      }));
      setDb(p=>({...p,productionLogs:[...(p.productionLogs||[]),...entries]}));
      for (const e of entries) await saveToDB('productionLog', e);
      setLines([]); setSaving(false);
      notify(`${entries.length} line(s) submitted for review`);
    };

    // Summary: PO vs what's been entered
    const summaryByColorSize = {};
    (po?.matrix||[]).forEach(m=>{
      const k=`${m.color}|${m.size}`;
      if (!summaryByColorSize[k]) summaryByColorSize[k]={ ...m, poQty:num(m.qty), packed:0 };
    });
    poLogs.filter(l=>l.stage==='Packing').forEach(l=>{
      const k=`${l.color}|${l.size}`;
      if (summaryByColorSize[k]) summaryByColorSize[k].packed+=num(l.qty);
    });

    return (
      <div>
        {/* PO selector */}
        <div style={{ background:'white', border:'1px solid #e6e9ef', borderRadius:10,
          padding:'14px 16px', marginBottom:16 }}>
          <label style={{ fontSize:11, fontWeight:800, color:'#676879',
            textTransform:'uppercase', letterSpacing:'0.05em', display:'block', marginBottom:6 }}>
            Select Purchase Order
          </label>
          <select value={selPO} onChange={e=>{setSelPO(e.target.value);setLines([]);}}
            style={{ padding:'10px 14px', border:'1.5px solid #e6e9ef', borderRadius:8,
              fontSize:13, width:'100%', maxWidth:420, outline:'none' }}>
            <option value="">— Choose a PO —</option>
            {myPOs.map(p=>(
              <option key={p.poNo} value={p.poNo}>
                PO {p.poNo} · {p.styleNo} · {num(p.qty).toLocaleString()} pcs · {p.brand}
              </option>
            ))}
          </select>
        </div>

        {selPO && (
          <>
            {/* Lock notice */}
            <div style={{ background:'#fff8e6', border:'1px solid #fde68a', borderRadius:8,
              padding:'9px 14px', marginBottom:14, fontSize:12, color:'#8a5a00',
              display:'flex', gap:8, alignItems:'flex-start' }}>
              <span style={{ fontSize:16 }}>🔒</span>
              <div>
                <b>Entry allowed for today and yesterday only.</b> Once submitted, entries
                are reviewed by your QA. Rejected entries will be sent back with a reason.
                After 2 days entries are locked permanently.
              </div>
            </div>

            {/* Entry form */}
            <div style={{ background:'white', border:'1px solid #e6e9ef', borderRadius:10,
              overflow:'hidden', marginBottom:16 }}>
              <div style={{ padding:'11px 16px', background:'linear-gradient(90deg,#f8fafc,#f1f5f9)',
                borderBottom:'1px solid #e6e9ef', display:'flex', justifyContent:'space-between',
                alignItems:'center' }}>
                <span style={{ fontSize:11, fontWeight:800, textTransform:'uppercase',
                  letterSpacing:'0.06em', color:'#323338' }}>Enter Production</span>
                <span style={{ fontSize:11, color:'#676879' }}>
                  Today: {todayStr} · Yesterday: {yesterdayStr}
                </span>
              </div>
              <div style={{ padding:'14px 16px' }}>
                {lines.length === 0 ? (
                  <div style={{ textAlign:'center', padding:'20px 0' }}>
                    <button onClick={addLines}
                      style={{ padding:'12px 28px', background:'linear-gradient(135deg,#0073ea,#005bbf)',
                        color:'white', border:'none', borderRadius:8, fontSize:13, fontWeight:700,
                        cursor:'pointer', boxShadow:'0 2px 10px rgba(0,115,234,.3)' }}>
                      {po?.matrix?.length ? '📋 Auto-fill from PO Matrix' : '+ Add Production Lines'}
                    </button>
                    {po?.matrix?.length && (
                      <div style={{ fontSize:11, color:'#676879', marginTop:8 }}>
                        Will pre-fill {po.matrix.length} rows from PO color/size breakdown
                      </div>
                    )}
                  </div>
                ) : (
                  <>
                    <div style={{ overflowX:'auto' }}>
                      <table style={{ borderCollapse:'collapse', width:'100%', minWidth:560 }}>
                        <thead><tr style={{ background:'#f1f5f9' }}>
                          {['Date','Stage','Line No.','Color','Size','Qty (pcs)',''].map(h=>(
                            <th key={h} style={{ padding:'8px 9px', textAlign:'left', fontWeight:800,
                              borderBottom:'1px solid #e6e9ef', fontSize:11, color:'#676879' }}>{h}</th>
                          ))}
                        </tr></thead>
                        <tbody>{lines.map((l,i)=>{
                          const poRow = (po?.matrix||[]).find(m=>m.color===l.color&&m.size===l.size);
                          const poQ   = poRow ? num(poRow.qty) : null;
                          const alreadyPacked = poLogs.filter(x=>x.stage===l.stage&&x.color===l.color&&x.size===l.size)
                            .reduce((a,x)=>a+num(x.qty),0);
                          const remaining = poQ !== null ? poQ - alreadyPacked : null;
                          return (
                            <tr key={i} style={{ borderBottom:'1px solid #f1f5f9' }}>
                              <td style={{ padding:'6px 8px' }}>
                                <select value={l.date} onChange={e=>upd(i,'date',e.target.value)}
                                  style={{ padding:'7px 8px', border:'1px solid #e6e9ef',
                                    borderRadius:6, fontSize:12, width:120 }}>
                                  <option value={todayStr}>Today ({todayStr})</option>
                                  <option value={yesterdayStr}>Yesterday</option>
                                </select>
                              </td>
                              <td style={{ padding:'6px 8px' }}>
                                <select value={l.stage} onChange={e=>upd(i,'stage',e.target.value)}
                                  style={{ padding:'7px 8px', border:'1px solid #e6e9ef',
                                    borderRadius:6, fontSize:12 }}>
                                  {STAGES.map(s=><option key={s}>{s}</option>)}
                                </select>
                              </td>
                              <td style={{ padding:'6px 8px' }}>
                                <input value={l.line} onChange={e=>upd(i,'line',e.target.value)}
                                  placeholder="e.g. L-01"
                                  style={{ width:70, padding:'7px 8px', border:'1px solid #e6e9ef',
                                    borderRadius:6, fontSize:12 }}/>
                              </td>
                              <td style={{ padding:'6px 8px' }}>
                                {colors.length
                                  ? <select value={l.color} onChange={e=>upd(i,'color',e.target.value)}
                                      style={{ padding:'7px 8px', border:'1px solid #e6e9ef',
                                        borderRadius:6, fontSize:12 }}>
                                      {colors.map(c=><option key={c}>{c}</option>)}
                                    </select>
                                  : <input value={l.color} onChange={e=>upd(i,'color',e.target.value)}
                                      placeholder="Color"
                                      style={{ width:90, padding:'7px 8px', border:'1px solid #e6e9ef',
                                        borderRadius:6, fontSize:12 }}/>}
                              </td>
                              <td style={{ padding:'6px 8px' }}>
                                {sizes.length
                                  ? <select value={l.size} onChange={e=>upd(i,'size',e.target.value)}
                                      style={{ padding:'7px 8px', border:'1px solid #e6e9ef',
                                        borderRadius:6, fontSize:12 }}>
                                      {sizes.map(s=><option key={s}>{s}</option>)}
                                    </select>
                                  : <input value={l.size} onChange={e=>upd(i,'size',e.target.value)}
                                      placeholder="Size"
                                      style={{ width:65, padding:'7px 8px', border:'1px solid #e6e9ef',
                                        borderRadius:6, fontSize:12 }}/>}
                              </td>
                              <td style={{ padding:'6px 8px' }}>
                                <div>
                                  <input type="number" value={l.qty}
                                    onChange={e=>upd(i,'qty',e.target.value)}
                                    placeholder="0"
                                    style={{ width:90, padding:'7px 8px', border:'1.5px solid #e6e9ef',
                                      borderRadius:6, fontSize:13, fontWeight:700, textAlign:'center' }}/>
                                  {remaining !== null && (
                                    <div style={{ fontSize:9.5, color:'#676879', marginTop:2 }}>
                                      Balance: {remaining.toLocaleString()}
                                    </div>
                                  )}
                                </div>
                              </td>
                              <td style={{ padding:'6px 6px' }}>
                                <button onClick={()=>setLines(p=>p.filter((_,xi)=>xi!==i))}
                                  style={{ background:'none', border:'none', color:T.danger,
                                    cursor:'pointer', fontSize:18, lineHeight:1 }}>×</button>
                              </td>
                            </tr>
                          );
                        })}</tbody>
                      </table>
                    </div>
                    <div style={{ display:'flex', gap:10, marginTop:14, flexWrap:'wrap' }}>
                      <button onClick={()=>setLines(p=>[...p,{ stage:'Packing', line:'',
                        color:colors[0]||'', size:sizes[0]||'', qty:'', date:todayStr }])}
                        style={{ padding:'8px 16px', background:'white', border:'1.5px solid #0073ea',
                          color:'#0073ea', borderRadius:7, fontSize:12, fontWeight:700, cursor:'pointer' }}>
                        + Add Row
                      </button>
                      <button onClick={submit} disabled={saving||!lines.some(l=>parseInt(l.qty)>0)}
                        style={{ padding:'10px 28px', fontSize:13, fontWeight:800, cursor:'pointer',
                          border:'none', borderRadius:8, color:'white',
                          background:saving?'#cbd5e1':'linear-gradient(135deg,#00c875,#009d5a)',
                          boxShadow:saving?'none':'0 2px 12px rgba(0,200,117,.35)' }}>
                        {saving?'Saving…':'✓ Submit for QA Review'}
                      </button>
                    </div>
                  </>
                )}
              </div>
            </div>

            {/* Summary: PO vs Packed */}
            {Object.keys(summaryByColorSize).length > 0 && (
              <div style={{ background:'white', border:'1px solid #e6e9ef',
                borderRadius:10, overflow:'hidden', marginBottom:16 }}>
                <div style={{ padding:'11px 16px', background:'linear-gradient(90deg,#f8fafc,#f1f5f9)',
                  borderBottom:'1px solid #e6e9ef', fontSize:11, fontWeight:800,
                  textTransform:'uppercase', letterSpacing:'0.06em', color:'#323338' }}>
                  PO vs Packing Summary
                </div>
                <div style={{ overflowX:'auto' }}>
                  <table style={{ borderCollapse:'collapse', fontSize:12, width:'100%' }}>
                    <thead><tr style={{ background:'#f8fafc' }}>
                      {['Color','Country','Size','PO Qty','Packed','Balance','%'].map(h=>(
                        <th key={h} style={{ padding:'7px 10px', textAlign:'left', fontWeight:800,
                          borderBottom:'1px solid #e6e9ef', fontSize:11, color:'#676879' }}>{h}</th>
                      ))}
                    </tr></thead>
                    <tbody>{Object.values(summaryByColorSize).map((r,i)=>{
                      const pct = r.poQty?Math.round(r.packed/r.poQty*100):0;
                      const ok  = r.packed >= r.poQty;
                      return (
                        <tr key={i} style={{ borderBottom:'1px solid #f1f5f9',
                          background:ok?'#f0fdf4':r.packed>0?'#fff8e6':'white' }}>
                          <td style={{ padding:'7px 10px', fontWeight:600 }}>{r.color||'—'}</td>
                          <td style={{ padding:'7px 10px' }}>{r.country||'—'}</td>
                          <td style={{ padding:'7px 10px' }}>{r.size||'—'}</td>
                          <td style={{ padding:'7px 10px', fontWeight:700 }}>{r.poQty.toLocaleString()}</td>
                          <td style={{ padding:'7px 10px', fontWeight:700,
                            color:ok?T.success:r.packed>0?T.warning:T.danger }}>
                            {r.packed.toLocaleString()}
                          </td>
                          <td style={{ padding:'7px 10px', fontWeight:700,
                            color:ok?T.success:T.danger }}>
                            {(r.poQty-r.packed).toLocaleString()}
                          </td>
                          <td style={{ padding:'7px 10px' }}>
                            <span style={{ fontWeight:800, color:ok?T.success:r.packed>0?T.warning:T.danger }}>
                              {pct}%
                            </span>
                          </td>
                        </tr>
                      );
                    })}</tbody>
                  </table>
                </div>
              </div>
            )}

            {/* My submitted logs */}
            {poLogs.length > 0 && (
              <div style={{ background:'white', border:'1px solid #e6e9ef', borderRadius:10, overflow:'hidden' }}>
                <div style={{ padding:'11px 16px', background:'linear-gradient(90deg,#f8fafc,#f1f5f9)',
                  borderBottom:'1px solid #e6e9ef', fontSize:11, fontWeight:800,
                  textTransform:'uppercase', letterSpacing:'0.06em', color:'#323338' }}>
                  Submitted Entries — PO {selPO}
                </div>
                <div style={{ overflowX:'auto' }}>
                  <table style={{ borderCollapse:'collapse', fontSize:11.5, width:'100%' }}>
                    <thead><tr style={{ background:'#f8fafc' }}>
                      {['Date','Stage','Color','Size','Qty','Status','QA Note'].map(h=>(
                        <th key={h} style={{ padding:'6px 10px', textAlign:'left', fontWeight:800,
                          borderBottom:'1px solid #e6e9ef', fontSize:11, color:'#676879' }}>{h}</th>
                      ))}
                    </tr></thead>
                    <tbody>{[...poLogs].sort((a,b)=>b.date.localeCompare(a.date)).map(r=>(
                      <tr key={r.id} style={{ borderBottom:'1px solid #f1f5f9',
                        background:r.status==='Rejected'?'#fdeaec':r.status==='Approved'?'#f0fdf4':'white' }}>
                        <td style={{ padding:'6px 10px' }}>{r.date}</td>
                        <td style={{ padding:'6px 10px' }}>{r.stage}</td>
                        <td style={{ padding:'6px 10px' }}>{r.color||'—'}</td>
                        <td style={{ padding:'6px 10px' }}>{r.size||'—'}</td>
                        <td style={{ padding:'6px 10px', fontWeight:700 }}>{num(r.qty).toLocaleString()}</td>
                        <td style={{ padding:'6px 10px' }}><Badge s={r.status||'Submitted'}/></td>
                        <td style={{ padding:'6px 10px', fontSize:11, color:T.danger }}>
                          {r.rejectionReason||'—'}
                        </td>
                      </tr>
                    ))}</tbody>
                  </table>
                </div>
              </div>
            )}
          </>
        )}
      </div>
    );
  };

  // ════════════════════════════════════════════════════════════════
  // TAB: INSPECTION OFFER
  // ════════════════════════════════════════════════════════════════
  const InspectionOfferTab = () => {
    const [f, setF] = useState({
      poNo:selPO||'', offeredQty:'', stage:'Final Inspection',
      preferredDate:todayStr, notes:'', breakdown:[],
      ppmFile:null, packingListFile:null, testReportFile:null,
      cartonChartFile:null, invoiceFile:null,
    });
    const [submitting, setSubmitting] = useState(false);
    const po      = myPOs.find(p=>p.poNo===f.poNo);
    const poLogs  = myLogs.filter(l=>l.supplier===supplierName&&l.poNo===f.poNo&&l.stage==='Packing');
    const packed  = poLogs.reduce((a,l)=>a+num(l.qty),0);

    // Auto-build breakdown from PO matrix
    const initBreakdown = (poNo) => {
      const p = myPOs.find(x=>x.poNo===poNo);
      if (!p?.matrix) return [];
      const byCS = {};
      p.matrix.forEach(m=>{
        const k=`${m.color}|${m.size}`;
        if (!byCS[k]) byCS[k]={ color:m.color, size:m.size, country:m.country,
          poQty:num(m.qty), packedQty:0, offeredQty:'' };
      });
      poLogs.forEach(l=>{
        const k=`${l.color}|${l.size}`;
        if (byCS[k]) byCS[k].packedQty+=num(l.qty);
      });
      return Object.values(byCS);
    };

    const selectPO = (poNo) => {
      setF(p=>({ ...p, poNo, breakdown:initBreakdown(poNo) }));
    };

    const updBD = (i,k,v) => setF(p=>({...p, breakdown:p.breakdown.map((x,xi)=>xi===i?{...x,[k]:v}:x)}));

    const STAGES = ['In-Line Inspection','Pre-Final Inspection','Final Inspection','Re-Inspection'];

    const submit = async () => {
      if (!f.poNo || !f.offeredQty) return alert('Select PO and enter offered qty');
      setSubmitting(true);
      const entry = {
        id:uid(), supplier:supplierName, poNo:f.poNo, styleNo:po?.styleNo||'',
        brand:po?.brand||'', stage:f.stage, offeredQty:num(f.offeredQty),
        preferredDate:f.preferredDate, notes:f.notes, breakdown:f.breakdown,
        ppmFile:f.ppmFile, packingListFile:f.packingListFile, testReportFile:f.testReportFile,
        cartonChartFile:f.cartonChartFile, invoiceFile:f.invoiceFile,
        submittedBy:currentUser.name, submittedAt:new Date().toISOString(),
        status:'Pending', qaResponse:null,
      };
      setDb(p=>({...p, inspectionOffers:[...(p.inspectionOffers||[]),entry]}));
      await saveToDB('inspectionOffer', entry);
      setF({ poNo:'', offeredQty:'', stage:'Final Inspection', preferredDate:todayStr,
        notes:'', breakdown:[], ppmFile:null, packingListFile:null,
        testReportFile:null, cartonChartFile:null, invoiceFile:null });
      setSubmitting(false);
      notify('Inspection offer submitted — your QA will respond shortly');
    };

    return (
      <div>
        <div style={{ background:'#eef4ff', border:'1px solid #c3d4f0', borderRadius:8,
          padding:'10px 14px', marginBottom:14, fontSize:12, color:'#0050a0' }}>
          🔍 <b>Invite your QA for inspection.</b> Fill the offered qty breakdown, upload all required documents,
          and submit. Your QA will receive a task notification and respond with a scheduled date.
        </div>

        {/* PO + basic offer details */}
        <div style={{ background:'white', border:'1px solid #e6e9ef', borderRadius:10,
          padding:'16px', marginBottom:14 }}>
          <div style={{ fontWeight:800, fontSize:12, color:'#676879', textTransform:'uppercase',
            letterSpacing:'0.05em', marginBottom:12 }}>Offer Details</div>
          <div style={{ display:'grid', gridTemplateColumns:'repeat(auto-fill,minmax(200px,1fr))', gap:14 }}>
            <div>
              <label style={{ fontSize:11, fontWeight:700, color:'#676879', display:'block', marginBottom:4 }}>PO Number *</label>
              <select value={f.poNo} onChange={e=>selectPO(e.target.value)}
                style={{ width:'100%', padding:'9px 12px', border:'1.5px solid #e6e9ef',
                  borderRadius:8, fontSize:13, outline:'none' }}>
                <option value="">— Select PO —</option>
                {myPOs.map(p=><option key={p.poNo} value={p.poNo}>PO {p.poNo} · {p.styleNo}</option>)}
              </select>
            </div>
            <div>
              <label style={{ fontSize:11, fontWeight:700, color:'#676879', display:'block', marginBottom:4 }}>Inspection Type *</label>
              <select value={f.stage} onChange={e=>setF(p=>({...p,stage:e.target.value}))}
                style={{ width:'100%', padding:'9px 12px', border:'1.5px solid #e6e9ef', borderRadius:8, fontSize:13, outline:'none' }}>
                {STAGES.map(s=><option key={s}>{s}</option>)}
              </select>
            </div>
            <div>
              <label style={{ fontSize:11, fontWeight:700, color:'#676879', display:'block', marginBottom:4 }}>Offered Qty (pcs) *</label>
              <input type="number" value={f.offeredQty} onChange={e=>setF(p=>({...p,offeredQty:e.target.value}))}
                placeholder={packed?`${packed.toLocaleString()} pcs packed`:'Enter qty'}
                style={{ width:'100%', padding:'9px 12px', border:'1.5px solid #e6e9ef',
                  borderRadius:8, fontSize:13, outline:'none', fontWeight:700 }}/>
              {packed>0 && <div style={{ fontSize:10.5, color:'#676879', marginTop:3 }}>
                Packed (declared): {packed.toLocaleString()} pcs
              </div>}
            </div>
            <div>
              <label style={{ fontSize:11, fontWeight:700, color:'#676879', display:'block', marginBottom:4 }}>Preferred Inspection Date</label>
              <input type="date" value={f.preferredDate} onChange={e=>setF(p=>({...p,preferredDate:e.target.value}))}
                style={{ width:'100%', padding:'9px 12px', border:'1.5px solid #e6e9ef',
                  borderRadius:8, fontSize:13, outline:'none' }}/>
            </div>
          </div>
          <div style={{ marginTop:12 }}>
            <label style={{ fontSize:11, fontWeight:700, color:'#676879', display:'block', marginBottom:4 }}>Notes / Special Instructions</label>
            <textarea value={f.notes} onChange={e=>setF(p=>({...p,notes:e.target.value}))} rows={2}
              placeholder="e.g. Re-inspection request, partial shipment, specific carton range to check…"
              style={{ width:'100%', padding:'9px 12px', border:'1.5px solid #e6e9ef',
                borderRadius:8, fontSize:13, outline:'none', resize:'vertical', boxSizing:'border-box' }}/>
          </div>
        </div>

        {/* Qty Breakdown — PO vs Packed vs Offered */}
        {f.breakdown.length > 0 && (
          <div style={{ background:'white', border:'1px solid #e6e9ef', borderRadius:10,
            overflow:'hidden', marginBottom:14 }}>
            <div style={{ padding:'11px 16px', background:'linear-gradient(90deg,#f8fafc,#f1f5f9)',
              borderBottom:'1px solid #e6e9ef', fontSize:11, fontWeight:800,
              textTransform:'uppercase', letterSpacing:'0.06em', color:'#323338' }}>
              Offered Qty Breakdown — Style / PO / Country / Color / Size
            </div>
            <div style={{ overflowX:'auto' }}>
              <table style={{ borderCollapse:'collapse', fontSize:12, width:'100%', minWidth:600 }}>
                <thead><tr style={{ background:'#f8fafc' }}>
                  {['Color','Country','Size','PO Qty','Packed (Declared)','Offered for Inspection','Variance'].map(h=>(
                    <th key={h} style={{ padding:'7px 10px', textAlign:'left', fontWeight:800,
                      borderBottom:'1px solid #e6e9ef', fontSize:11, color:'#676879' }}>{h}</th>
                  ))}
                </tr></thead>
                <tbody>{f.breakdown.map((r,i)=>{
                  const offered = parseInt(r.offeredQty)||0;
                  const diff    = offered - r.poQty;
                  return (
                    <tr key={i} style={{ borderBottom:'1px solid #f1f5f9' }}>
                      <td style={{ padding:'7px 10px', fontWeight:600 }}>{r.color||'—'}</td>
                      <td style={{ padding:'7px 10px' }}>{r.country||'—'}</td>
                      <td style={{ padding:'7px 10px' }}>{r.size||'—'}</td>
                      <td style={{ padding:'7px 10px', fontWeight:700 }}>{r.poQty.toLocaleString()}</td>
                      <td style={{ padding:'7px 10px',
                        color:r.packedQty>=r.poQty?T.success:T.warning, fontWeight:700 }}>
                        {r.packedQty.toLocaleString()}
                      </td>
                      <td style={{ padding:'7px 8px' }}>
                        <input type="number" value={r.offeredQty}
                          onChange={e=>updBD(i,'offeredQty',e.target.value)}
                          placeholder={r.packedQty||r.poQty}
                          style={{ width:90, padding:'5px 8px', border:'1.5px solid #e6e9ef',
                            borderRadius:6, fontSize:12, fontWeight:700, textAlign:'center' }}/>
                      </td>
                      <td style={{ padding:'7px 10px', fontWeight:700,
                        color:diff>=0?T.success:T.danger }}>
                        {offered>0?(diff>=0?'+':'')+diff.toLocaleString():'—'}
                      </td>
                    </tr>
                  );
                })}</tbody>
                <tfoot><tr style={{ background:'#f8fafc', fontWeight:800 }}>
                  <td colSpan={3} style={{ padding:'8px 10px', fontSize:12 }}>TOTAL</td>
                  <td style={{ padding:'8px 10px' }}>{f.breakdown.reduce((a,r)=>a+r.poQty,0).toLocaleString()}</td>
                  <td style={{ padding:'8px 10px' }}>{f.breakdown.reduce((a,r)=>a+r.packedQty,0).toLocaleString()}</td>
                  <td style={{ padding:'8px 10px', color:T.primary, fontWeight:900 }}>
                    {f.breakdown.reduce((a,r)=>a+(parseInt(r.offeredQty)||0),0).toLocaleString()}
                  </td>
                  <td></td>
                </tr></tfoot>
              </table>
            </div>
          </div>
        )}

        {/* Document uploads */}
        <div style={{ background:'white', border:'1px solid #e6e9ef', borderRadius:10,
          padding:'16px', marginBottom:14 }}>
          <div style={{ fontWeight:800, fontSize:12, color:'#676879', textTransform:'uppercase',
            letterSpacing:'0.05em', marginBottom:12 }}>Required Documents</div>
          <div style={{ display:'grid', gridTemplateColumns:'repeat(auto-fill,minmax(240px,1fr))', gap:14 }}>
            {[
              { key:'ppmFile',         label:'PPM Meeting Form',    required:true  },
              { key:'packingListFile', label:'Packing List',        required:true  },
              { key:'testReportFile',  label:'Test Reports',        required:false },
              { key:'cartonChartFile', label:'Carton Chart',        required:false },
              { key:'invoiceFile',     label:'Invoice / Proforma',  required:false },
            ].map(({ key, label, required }) => (
              <div key={key} style={{ border:`1.5px solid ${f[key]?T.success:'#e6e9ef'}`,
                borderRadius:8, padding:'12px', background:f[key]?'#f0fdf4':'#fafbfc' }}>
                <div style={{ fontSize:11.5, fontWeight:700, marginBottom:6 }}>
                  {label} {required&&<span style={{ color:T.danger }}>*</span>}
                </div>
                {f[key] ? (
                  <div style={{ display:'flex', alignItems:'center', justifyContent:'space-between' }}>
                    <span style={{ fontSize:11, color:T.success }}>
                      ✓ {f[key].name.slice(0,28)}{f[key].name.length>28?'…':''}
                    </span>
                    <button onClick={()=>setF(p=>({...p,[key]:null}))}
                      style={{ background:'none', border:'none', color:T.danger,
                        cursor:'pointer', fontSize:16 }}>×</button>
                  </div>
                ) : (
                  <UploadBtn label={`Upload ${label}`}
                    meta={{ poNo:f.poNo, supplier:supplierName }}
                    onDone={file=>setF(p=>({...p,[key]:file}))}/>
                )}
              </div>
            ))}
          </div>
        </div>

        {/* Submit button */}
        <button onClick={submit} disabled={submitting||!f.poNo||!f.offeredQty}
          style={{ padding:'12px 32px', fontSize:14, fontWeight:800, cursor:'pointer',
            border:'none', borderRadius:9, color:'white', width:'100%',
            background:submitting||!f.poNo?'#cbd5e1':'linear-gradient(135deg,#0073ea,#005bbf)',
            boxShadow:submitting||!f.poNo?'none':'0 3px 14px rgba(0,115,234,.35)' }}>
          {submitting?'Submitting…':'🔍 Submit Inspection Offer to QA'}
        </button>

        {/* My previous offers */}
        {myOffers.length > 0 && (
          <div style={{ marginTop:20, background:'white', border:'1px solid #e6e9ef',
            borderRadius:10, overflow:'hidden' }}>
            <div style={{ padding:'11px 16px', background:'linear-gradient(90deg,#f8fafc,#f1f5f9)',
              borderBottom:'1px solid #e6e9ef', fontSize:11, fontWeight:800,
              textTransform:'uppercase', letterSpacing:'0.06em' }}>My Inspection Offers</div>
            <div style={{ overflowX:'auto' }}>
              <table style={{ borderCollapse:'collapse', fontSize:12, width:'100%' }}>
                <thead><tr style={{ background:'#f8fafc' }}>
                  {['Submitted','PO','Stage','Offered Qty','Preferred Date','Status','QA Response'].map(h=>(
                    <th key={h} style={{ padding:'7px 10px', fontWeight:800, borderBottom:'1px solid #e6e9ef', fontSize:11, color:'#676879' }}>{h}</th>
                  ))}
                </tr></thead>
                <tbody>{[...myOffers].sort((a,b)=>b.submittedAt?.localeCompare(a.submittedAt)).map(r=>(
                  <tr key={r.id} style={{ borderBottom:'1px solid #f1f5f9' }}>
                    <td style={{ padding:'7px 10px' }}>{r.submittedAt?.split('T')[0]||r.date}</td>
                    <td style={{ padding:'7px 10px', fontWeight:700 }}>{r.poNo}</td>
                    <td style={{ padding:'7px 10px' }}>{r.stage}</td>
                    <td style={{ padding:'7px 10px', fontWeight:700 }}>{num(r.offeredQty).toLocaleString()}</td>
                    <td style={{ padding:'7px 10px' }}>{r.preferredDate}</td>
                    <td style={{ padding:'7px 10px' }}><Badge s={r.status}/></td>
                    <td style={{ padding:'7px 10px', fontSize:11, color:T.textSub }}>
                      {r.qaResponse||'Awaiting response'}
                    </td>
                  </tr>
                ))}</tbody>
              </table>
            </div>
          </div>
        )}
      </div>
    );
  };

  // ════════════════════════════════════════════════════════════════
  // TAB: DOCUMENTS
  // ════════════════════════════════════════════════════════════════
  const DocumentsTab = () => {
    const [uploading, setUploading] = useState(false);
    const [docType, setDocType]     = useState('Test Report');
    const [docPO, setDocPO]         = useState('');
    const DOC_TYPES = ['PPM Meeting Form','Packing List','Test Report','Carton Chart',
      'Invoice','Certificate','Compliance Document','Other'];

    return (
      <div>
        {/* Upload new doc */}
        <div style={{ background:'white', border:'1px solid #e6e9ef', borderRadius:10,
          padding:'16px', marginBottom:16 }}>
          <div style={{ fontWeight:800, fontSize:12, color:'#676879', textTransform:'uppercase',
            letterSpacing:'0.05em', marginBottom:12 }}>Upload Document</div>
          <div style={{ display:'grid', gridTemplateColumns:'repeat(auto-fill,minmax(180px,1fr))', gap:12, marginBottom:12 }}>
            <div>
              <label style={{ fontSize:11, fontWeight:700, color:'#676879', display:'block', marginBottom:4 }}>Document Type</label>
              <select value={docType} onChange={e=>setDocType(e.target.value)}
                style={{ width:'100%', padding:'9px 12px', border:'1.5px solid #e6e9ef', borderRadius:8, fontSize:13 }}>
                {DOC_TYPES.map(d=><option key={d}>{d}</option>)}
              </select>
            </div>
            <div>
              <label style={{ fontSize:11, fontWeight:700, color:'#676879', display:'block', marginBottom:4 }}>Related PO (optional)</label>
              <select value={docPO} onChange={e=>setDocPO(e.target.value)}
                style={{ width:'100%', padding:'9px 12px', border:'1.5px solid #e6e9ef', borderRadius:8, fontSize:13 }}>
                <option value="">General / Not PO-specific</option>
                {myPOs.map(p=><option key={p.poNo} value={p.poNo}>PO {p.poNo}</option>)}
              </select>
            </div>
          </div>
          <UploadBtn label="Select & Upload File"
            meta={{ poNo:docPO, supplier:supplierName, docType }}
            onDone={async (file) => {
              const entry = { id:uid(), supplier:supplierName, poNo:docPO, docType,
                name:file.name, url:file.url, uploadedBy:currentUser.name,
                uploadedAt:new Date().toISOString(), status:'Uploaded' };
              setDb(p=>({...p, supplierSubmissions:[...(p.supplierSubmissions||[]),entry]}));
              await saveToDB('supplierSubmission', entry);
              notify(`${file.name} uploaded successfully`);
            }}/>
        </div>

        {/* Document list */}
        <div style={{ background:'white', border:'1px solid #e6e9ef', borderRadius:10, overflow:'hidden' }}>
          <div style={{ padding:'11px 16px', background:'linear-gradient(90deg,#f8fafc,#f1f5f9)',
            borderBottom:'1px solid #e6e9ef', fontSize:11, fontWeight:800,
            textTransform:'uppercase', letterSpacing:'0.06em' }}>
            My Uploaded Documents ({myDocs.length})
          </div>
          {myDocs.length===0 ? (
            <div style={{ padding:24, textAlign:'center', color:'#676879', fontSize:12 }}>
              No documents uploaded yet.
            </div>
          ) : (
            <div style={{ overflowX:'auto' }}>
              <table style={{ borderCollapse:'collapse', fontSize:12, width:'100%' }}>
                <thead><tr style={{ background:'#f8fafc' }}>
                  {['Date','Document Type','PO','File Name','Status',''].map(h=>(
                    <th key={h} style={{ padding:'7px 10px', fontWeight:800, borderBottom:'1px solid #e6e9ef', fontSize:11, color:'#676879' }}>{h}</th>
                  ))}
                </tr></thead>
                <tbody>{[...myDocs].sort((a,b)=>(b.uploadedAt||'').localeCompare(a.uploadedAt||'')).map(r=>(
                  <tr key={r.id} style={{ borderBottom:'1px solid #f1f5f9' }}>
                    <td style={{ padding:'7px 10px' }}>{r.uploadedAt?.split('T')[0]||'—'}</td>
                    <td style={{ padding:'7px 10px', fontWeight:600 }}>{r.docType}</td>
                    <td style={{ padding:'7px 10px' }}>{r.poNo||'General'}</td>
                    <td style={{ padding:'7px 10px', fontSize:11 }}>{r.name}</td>
                    <td style={{ padding:'7px 10px' }}><Badge s={r.status||'Uploaded'}/></td>
                    <td style={{ padding:'7px 10px' }}>
                      <a href={r.url} target="_blank" rel="noopener noreferrer"
                        style={{ color:T.primary, fontSize:11, fontWeight:700, textDecoration:'none' }}>
                        ↗ View
                      </a>
                    </td>
                  </tr>
                ))}</tbody>
              </table>
            </div>
          )}
        </div>
      </div>
    );
  };

  // ════════════════════════════════════════════════════════════════
  return (
    <div style={{ padding:'20px 22px 70px', maxWidth:960, margin:'0 auto' }}>
      {/* Header */}
      <div style={{ background:'linear-gradient(135deg,#12161c,#0073ea)', color:'white',
        borderRadius:14, padding:'18px 22px', marginBottom:20,
        display:'flex', justifyContent:'space-between', alignItems:'center', flexWrap:'wrap', gap:12 }}>
        <div>
          <div style={{ fontWeight:900, fontSize:19 }}>🏭 {supplierName}</div>
          <div style={{ fontSize:11.5, opacity:0.8, marginTop:2 }}>
            Supplier Portal · {todayStr}
          </div>
        </div>
        <div style={{ display:'flex', gap:16 }}>
          {[['FPR', fpr!==null?`${fpr}%`:'—'],
            ['DHU', dhu||'—'],
            ['Open POs', myPOs.length],
            ['Offers', myOffers.filter(o=>o.status==='Pending').length+' pending']
          ].map(([l,v])=>(
            <div key={l} style={{ textAlign:'center' }}>
              <div style={{ fontSize:18, fontWeight:900 }}>{v}</div>
              <div style={{ fontSize:9.5, opacity:0.7, textTransform:'uppercase', letterSpacing:'0.05em' }}>{l}</div>
            </div>
          ))}
        </div>
      </div>

      {/* Tabs */}
      <div style={{ display:'flex', gap:2, borderBottom:'2px solid #e6e9ef',
        marginBottom:20, overflowX:'auto' }}>
        {TABS.map(t=>(
          <button key={t} onClick={()=>setTab(t)}
            style={{ padding:'9px 16px', border:'none', cursor:'pointer', fontSize:11.5,
              fontWeight:800, whiteSpace:'nowrap', borderRadius:'6px 6px 0 0',
              background:tab===t?'linear-gradient(135deg,#0073ea,#005bbf)':'transparent',
              color:tab===t?'white':'#676879',
              boxShadow:tab===t?'0 -2px 8px rgba(0,115,234,.2)':'none' }}>
            {t}
          </button>
        ))}
      </div>

      {tab==='DASHBOARD'         && <DashboardTab/>}
      {tab==='MY POs'            && <POsTab/>}
      {tab==='PRODUCTION'        && <ProductionTab/>}
      {tab==='INSPECTION OFFER'  && <InspectionOfferTab/>}
      {tab==='DOCUMENTS'         && <DocumentsTab/>}

      {toast && (
        <div style={{ position:'fixed', bottom:24, right:24, zIndex:3000, background:'white',
          borderLeft:`4px solid ${toast.type==='success'?T.success:T.warning}`,
          borderRadius:10, padding:'13px 20px', boxShadow:'0 8px 32px rgba(0,0,0,.18)',
          fontSize:13, fontWeight:600, maxWidth:360 }}>
          {toast.msg}
        </div>
      )}
    </div>
  );
}
